import { useMemo, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';
import {
  CATEGORY_TINT, QUESTION_TYPES,
  type QuestionCategory, type QuestionType, type QuestionTypeMeta,
} from '../lib/types';
import { useBuilder } from '../lib/builderStore';
import { Icon, type IconName } from '../components/icons';

const CATEGORY_ORDER: QuestionCategory[] =
  ['Text', 'Choice', 'Scale', 'Date', 'Advanced', 'Layout'];

export function Palette({ onAdd }: { onAdd: (type: QuestionType) => void }) {
  const [filter, setFilter] = useState('');
  const definition = useBuilder((s) => s.definition);
  const activePageRef = useBuilder((s) => s.activePageRef);
  const addRepeatingGroup = useBuilder((s) => s.addRepeatingGroup);

  const activePage = definition?.pages.find((p) => p.ref === activePageRef);
  const activeIndex = definition?.pages.findIndex((p) => p.ref === activePageRef) ?? -1;

  const grouped = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const matches = QUESTION_TYPES.filter(
      (t) => !needle || t.label.toLowerCase().includes(needle) || t.type.includes(needle),
    );

    return CATEGORY_ORDER
      .map((category) => ({ category, items: matches.filter((t) => t.category === category) }))
      .filter((group) => group.items.length > 0);
  }, [filter]);

  return (
    <div className="flex h-full flex-col bg-[var(--ff-surface)]">
      <div className="border-b border-[var(--ff-border)] p-3">
        <div className="relative">
          <Icon
            name="search"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2
                       text-[var(--ff-text-muted)]"
          />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search question types"
            aria-label="Search question types"
            className="w-full rounded-md border border-[var(--ff-border-strong)] bg-transparent
                       py-2 pl-8 pr-3 text-sm placeholder:text-[var(--ff-text-muted)]"
          />
        </div>

        {/* Says where a click will land, so "add" is never a guess. */}
        {activePage && (
          <p className="mt-2 flex items-center gap-1.5 text-[11px] text-[var(--ff-text-muted)]">
            <Icon name="plus" size={12} />
            Adds to
            <span className="font-medium text-[var(--ff-text)]">
              page {activeIndex + 1}
              {activePage.title ? ` · ${activePage.title}` : ''}
            </span>
          </p>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-2">
        {/* Not a question type, so it is not in QUESTION_TYPES and sits above the list
            rather than inside a category it does not belong to. */}
        {!filter.trim() && (
          <button
            onClick={() => addRepeatingGroup()}
            className="mb-3 flex w-full items-center gap-2 rounded-md border border-dashed
                       border-[var(--ff-border)] px-2.5 py-2 text-left text-[13px]
                       transition-colors hover:border-[var(--ff-primary)]
                       hover:text-[var(--ff-primary)]"
          >
            <Icon name="copy" size={14} />
            <span className="flex-1">
              Repeating group
              <span className="block text-[11px] text-[var(--ff-text-muted)]">
                Let respondents add more than one set of answers
              </span>
            </span>
          </button>
        )}

        {grouped.length === 0 && (
          <p className="px-2 py-6 text-center text-sm text-[var(--ff-text-muted)]">
            No question type matches “{filter}”.
          </p>
        )}

        {grouped.map((group) => (
          <section key={group.category} className="mb-3">
            <h3 className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-widest
                           text-[var(--ff-text-muted)]">
              {group.category}
            </h3>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.type}>
                  <PaletteItem item={item} onAdd={onAdd} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <p className="border-t border-[var(--ff-border)] px-3 py-2 text-[11px]
                    text-[var(--ff-text-muted)]">
        Click to add, or drag onto any page.
      </p>
    </div>
  );
}

function PaletteItem({
  item, onAdd,
}: { item: QuestionTypeMeta; onAdd: (type: QuestionType) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette:${item.type}`,
    data: { kind: 'palette', type: item.type },
    disabled: item.unavailable,
  });

  const tint = CATEGORY_TINT[item.category];

  if (item.unavailable) {
    return (
      <div
        title="The runtime cannot render this type yet"
        className="flex w-full cursor-not-allowed items-center gap-2.5 rounded-md px-2 py-1.5
                   text-sm opacity-45"
      >
        <span className={clsx('grid h-7 w-7 shrink-0 place-items-center rounded-md', tint)}>
          <Icon name={item.icon as IconName} />
        </span>
        <span className="truncate">{item.label}</span>
        <span className="ml-auto shrink-0 rounded border border-[var(--ff-border)] px-1
                         text-[9px] uppercase tracking-wide">
          soon
        </span>
      </div>
    );
  }

  return (
    // A button, not a drag-only tile: clicking must work for keyboard and screen-reader
    // users, since dragging can never be the only way to add a question (WCAG 2.5.7).
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => onAdd(item.type)}
      className={clsx(
        'group flex w-full cursor-grab items-center gap-2.5 rounded-md px-2 py-1.5 text-left',
        'text-sm transition-colors hover:bg-[var(--ff-surface-raised)] active:cursor-grabbing',
        isDragging && 'opacity-40',
      )}
    >
      <span className={clsx('grid h-7 w-7 shrink-0 place-items-center rounded-md', tint)}>
        <Icon name={item.icon as IconName} />
      </span>
      <span className="truncate">{item.label}</span>
      {item.piiCapable && (
        <span
          title="Can hold personal data"
          className="ml-auto shrink-0 rounded bg-[var(--ff-warning-soft)] px-1
                     text-[9px] font-semibold uppercase text-[var(--ff-warning)]"
        >
          pii
        </span>
      )}
    </button>
  );
}

/** Shown under the cursor while dragging from the palette. */
export function PalettePreview({ item }: { item: QuestionTypeMeta }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-[var(--ff-primary)]
                    bg-[var(--ff-surface)] px-3 py-2 text-sm font-medium shadow-xl">
      <span className={clsx(
        'grid h-7 w-7 place-items-center rounded-md', CATEGORY_TINT[item.category],
      )}>
        <Icon name={item.icon as IconName} />
      </span>
      {item.label}
    </div>
  );
}
